import { action, observable, computed, runInAction, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import uniq from 'lodash/uniq'
import { validationContext, func, pattern } from 'validx'
import { extractMessageFromError } from 'utils/error-util'
import InvitationStore from 'iam/InvitationStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import Team from 'iam/models/Team'
import { Id } from 'types/definitions'
import { AxiosError } from 'axios'
import TeamMember from 'iam/models/TeamMember'

interface InviteResults {
  email: string
  success?: boolean
  error?: AxiosError
}

export default class InviteTeamMemberModalViewModel {
  @observable showing = false
  @observable email = ''
  @observable teamRoleId = ''

  validation = validationContext(this, {
    email: [
      func(
        () => this.emailAddresses.length > 0,
        'Please enter at least one email address',
      ),
      func(() => {
        const fn = pattern('email')
        const invalidEmails = this.emailAddresses
          .map((e) => {
            const isValidEmail =
              fn({ obj: this, field: 'email', value: e }) === true
            return isValidEmail ? false : e
          })
          .filter(Boolean)
        return (
          invalidEmails.length === 0 ||
          `The following emails are invalid: ${invalidEmails.join(', ')}`
        )
      }),
      func(() => {
        const teamMemberEmails = this.currentTeamMemberEmailAddresses
        const alreadyExistsEmails = this.emailAddresses.filter((email) =>
          teamMemberEmails.has(email),
        )
        return (
          alreadyExistsEmails.length === 0 ||
          `The following emails already exist in this team: ${alreadyExistsEmails.join(
            ', ',
          )}`
        )
      }),
    ],
  })

  constructor(
    private invitationStore: InvitationStore,
    private teamRoleStore: TeamRoleStore,
    private team: Team,
    private getTeamMembers: () => TeamMember[],
    private reportInvitationSent: () => void,
  ) {
    makeObservable(this)
    this.sendInvite = this.sendInvite.bind(this)
  }

  @computed
  get teamRoles() {
    return this.teamRoleStore
      .forWorkspace(Id.assert(this.team.workspace.id))
      .filter((role) => !role.isOwnerRole)
  }

  @action.bound
  show(teamRoleId: string | null) {
    this.validation.reset()
    this.showing = true
    this.email = ''
    this.activate(teamRoleId)
  }

  @computed
  get currentTeamMemberEmailAddresses() {
    return new Set(this.getTeamMembers().map(({ member }) => member.email))
  }

  @computed
  get emailAddresses() {
    return uniq(
      this.email
        .split(/[,\n]+/g)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean),
    )
  }

  activate = task(async (teamRoleId: string | null) => {
    await this.teamRoleStore.fetchTeamRolesForWorkspace(
      Id.assert(this.team.workspace.id),
    )
    const role = this.teamRoles.find((x) => x.id === teamRoleId)
    if (role && !role.isOwnerRole && teamRoleId) {
      this.teamRoleId = teamRoleId
    } else {
      const role = this.teamRoles[0]
      this.teamRoleId = teamRoleId || (role && Id.assert(role.id))
    }
  })

  sendInvite = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const emails = this.emailAddresses
    const results: Array<InviteResults> = await Promise.all(
      emails.map((email) =>
        email === 'fail@test.com'
          ? Promise.resolve({
              email,
              error: new Error('Oh my, shit happened'),
            })
          : this.invitationStore
              .invite({
                workspace_id: Id.assert(this.team.workspace.id),
                team_id: Id.assert(this.team.id),
                team_role_id: Id.assert(this.teamRoleId),
                email,
                // hard code to portal
                target: 'Portal',
              })
              .then(() => {
                return { email, success: true }
              })
              .catch((err) => {
                return { email, error: err }
              }),
      ),
    )

    const failed = results.filter((r) => r.error)
    if (failed.length === 0) {
      this.reportInvitationSent()
      this.close()

      return
    }

    // Report failures, set the email field to the emails that failed.
    runInAction(() => {
      this.email = failed.map((f) => f.email).join(', ')
    })

    const errorMessages = failed
      .map((f) => extractMessageFromError(f.error!))
      .join('. ')

    console.error(errorMessages)
    this.close()
  })

  @action.bound
  close() {
    this.showing = false
  }
}
